<ng-template #hdImageLoadingTemplate>
  <div *ngIf="hdThumbnailLoading" class="loading-indicator-container">
    <astrobin-loading-indicator></astrobin-loading-indicator>
  </div>
</ng-template>

<ng-template #realThumbnailLoadingTemplate>
  <div *ngIf="realThumbnailLoading" class="loading-indicator-container">
    <astrobin-loading-indicator [progress]="realImageLoadingProgress$ | async"></astrobin-loading-indicator>
  </div>
</ng-template>

<ng-template #instructionTemplate let-icon="icon" let-message="message">
  <div class="instruction-container">
    <div class="instructions">
      <fa-icon [icon]="icon"></fa-icon>
      <span [innerHTML]="message"></span>
    </div>
  </div>
</ng-template>

<ng-template #dismissibleInstructionTemplate let-cookieKey="cookieKey" let-icon="icon" let-message="message">
  <div class="instruction-container">
    <div class="instructions">
      <fa-icon [icon]="icon"></fa-icon>
      <span [innerHTML]="message"></span>
      <button
        (click)="dismissInstruction(cookieKey)"
        class="dismiss-instruction-btn"
        aria-label="Dismiss instruction"
        type="button"
      >
        <fa-icon [icon]="['fas', 'times']"></fa-icon>
      </button>
    </div>
  </div>
</ng-template>

<ng-template #fullSizeLimitationTemplate>
  <ng-container
    [ngTemplateOutlet]="instructionTemplate"
    [ngTemplateOutletContext]="{
      icon: 'info-circle',
      message: 'Zoom disabled by the image owner.' | translate
    }"
  ></ng-container>
</ng-template>

<ng-template #imageTooLargeToZoomTemplate>
  <ng-container
    [ngTemplateOutlet]="instructionTemplate"
    [ngTemplateOutletContext]="{
      icon: 'warning',
      message:
        ('Image too large for the interactive zoom.' | translate) +
        ' ' +
        ('Click to view full size in a new tab.' | translate)
    }"
  ></ng-container>
</ng-template>

<ng-template #gifNoZoomTemplate>
  <ng-container
    [ngTemplateOutlet]="instructionTemplate"
    [ngTemplateOutletContext]="{
      icon: 'info-circle',
      message: 'Zooming is not available for GIF animations on touch devices.' | translate
    }"
  ></ng-container>
</ng-template>

<ng-template #pinchAndZoomInstructionTemplate>
  <ng-container *ngIf="!isInstructionDismissed('pinchAndZoom')">
    <ng-container
      [ngTemplateOutlet]="dismissibleInstructionTemplate"
      [ngTemplateOutletContext]="{
        icon: 'hand-paper',
        message: 'Pinch and zoom!' | translate,
        cookieKey: 'pinchAndZoom'
      }"
    ></ng-container>
  </ng-container>
</ng-template>

<ng-template #clickAndScrollInstructionTemplate>
  <ng-container *ngIf="!isInstructionDismissed('scrollToMagnify')">
    <ng-container
      [ngTemplateOutlet]="dismissibleInstructionTemplate"
      [ngTemplateOutletContext]="{
        icon: 'mouse-pointer',
        message: 'Scroll to magnify.' | translate,
        cookieKey: 'scrollToMagnify'
      }"
    ></ng-container>
  </ng-container>
</ng-template>

<ng-template #clickAgainToCloseLensInstructionTemplate>
  <ng-container
    [ngTemplateOutlet]="instructionTemplate"
    [ngTemplateOutletContext]="{
      icon: 'mouse-pointer',
      message: 'Click again to close lens.' | translate
    }"
  ></ng-container>
</ng-template>

<ng-template #zoomFrozenInstructionTemplate>
  <ng-container
    [ngTemplateOutlet]="instructionTemplate"
    [ngTemplateOutletContext]="{
      icon: 'lock',
      message: 'Zoom is frozen. Press F to unfreeze.' | translate
    }"
  ></ng-container>
</ng-template>

<ng-template #measureFirstPointInstructionTemplate>
  <ng-container
    [ngTemplateOutlet]="instructionTemplate"
    [ngTemplateOutletContext]="{
      icon: 'mouse-pointer',
      message: 'Click or start dragging to begin measurement.' | translate
    }"
  ></ng-container>
</ng-template>

<ng-template #measureSecondPointInstructionTemplate>
  <ng-container
    [ngTemplateOutlet]="instructionTemplate"
    [ngTemplateOutletContext]="{
      icon: 'mouse-pointer',
      message: 'Click or release to complete measurement.' | translate
    }"
  ></ng-container>
</ng-template>

<ng-template #staticTemplate let-hd="hd" let-real="real">
  <div class="static-template-container w-100 h-100 d-flex justify-content-center align-items-center">
    <ng-container *ngIf="!hd && !real" [ngTemplateOutlet]="hdImageLoadingTemplate"></ng-container>
    <!-- Only show real thumbnail loading indicator if we're not in annotation/measuring mode
         When in these modes, we can use the HD image immediately while real loads in background -->
    <ng-container *ngIf="hd && !real && allowReal" [ngTemplateOutlet]="realThumbnailLoadingTemplate"></ng-container>

    <!-- Loading indicator while the static image is loading -->
    <div *ngIf="!isStaticImageLoaded" class="loading-indicator-container">
      <astrobin-loading-indicator></astrobin-loading-indicator>
    </div>

    <!-- Simple static image element that will be passed to measuring/annotation tools -->
    <div #staticImageContainer class="static-image-container">
      <img
        #staticImage
        [src]="(isVeryLargeImage || !real || !allowReal || isMeasuringMode || isAnnotationMode) && hd ? hd : real"
        [style.max-width.px]="maxDisplayWidth"
        (load)="onStaticImageLoaded()"
        class="static-image"
        alt=""
      />
    </div>

    <!-- Show appropriate messages for different modes -->
    <ng-container *ngIf="isMeasuringMode">
      <ng-container
        *ngIf="!measurementInProgress"
        [ngTemplateOutlet]="measureFirstPointInstructionTemplate"
      ></ng-container>

      <ng-container
        *ngIf="measurementInProgress"
        [ngTemplateOutlet]="measureSecondPointInstructionTemplate"
      ></ng-container>
    </ng-container>

    <ng-container *ngIf="hd && !allowReal" [ngTemplateOutlet]="fullSizeLimitationTemplate"></ng-container>
  </div>

  <ng-container [ngTemplateOutlet]="zoomModesTemplate" [ngTemplateOutletContext]="{ real }"></ng-container>
</ng-template>

<ng-template #touchDeviceTemplate let-hd="hd" let-real="real">
  <div class="touch-device">
    <div
      *ngIf="!isVeryLargeImage && (hdThumbnailLoading || realThumbnailLoading || touchZoom.canvasLoading)"
      class="loading-indicator-container"
    >
      <astrobin-loading-indicator [progress]="loadingProgress$ | async"></astrobin-loading-indicator>
    </div>

    <ng-container *ngIf="!isVeryLargeImage">
      <!-- For GIFs, show a simple image without zooming capabilities -->
      <div *ngIf="isGif && real && allowReal" @fadeInOut class="gif-container">
        <img [src]="realThumbnailUnsafeUrl" class="gif-image" alt="" />
      </div>

      <!-- For regular images, use the canvas-based zoom -->
      <div
        #touchRealContainer
        *ngIf="!isGif && real && allowReal"
        @fadeInOut
        [attr.data-ignore-swipe-down]="touchZoom.touchScale > 1 ? true : null"
        (doubletap)="onDoubleTap($event)"
        (panend)="onPanEnd($event)"
        (panmove)="onPanMove($event)"
        (panstart)="onPanStart($event)"
        (pinchend)="onPinchEnd()"
        (pinchmove)="onPinchMove($event)"
        (pinchstart)="onPinchStart($event)"
        class="touch-real-container"
      >
        <canvas #touchRealCanvas class="touch-real-canvas"></canvas>

        <div
          *ngIf="
            touchZoom.actualTouchZoom !== null && !touchZoom.canvasLoading && !isMeasuringMode && !isAnnotationMode
          "
          [class.beyond-one]="Math.round(touchZoom.actualTouchZoom * 100) >= 101"
          (mouseenter)="setMouseOverUIElement(true)"
          (mouseleave)="setMouseOverUIElement(false)"
          class="touch-zoom-indicator"
        >
          {{ touchZoom.actualTouchZoom | number: "1.2-2" }}x
        </div>
      </div>
    </ng-container>

    <ng-container *ngIf="isVeryLargeImage">
      <img *ngIf="!real" [src]="hd" class="hd-image" alt="" />

      <a
        *ngIf="hd && real && realThumbnailUnsafeUrl"
        [href]="realThumbnailUnsafeUrl"
        class="no-external-link-icon d-flex align-items-center"
        target="_blank"
      >
        <img [src]="hd" class="hd-image" alt="" />
      </a>
      <ng-container
        *ngIf="hd && real && allowReal && realThumbnailUnsafeUrl"
        [ngTemplateOutlet]="imageTooLargeToZoomTemplate"
      ></ng-container>
    </ng-container>

    <!-- Show appropriate messages based on image type -->
    <ng-container *ngIf="isGif && allowReal && real" [ngTemplateOutlet]="gifNoZoomTemplate"></ng-container>
    <ng-container
      *ngIf="
        !isGif &&
        !isVeryLargeImage &&
        allowReal &&
        real &&
        !touchZoom.canvasLoading &&
        !isMeasuringMode &&
        !isAnnotationMode
      "
      [ngTemplateOutlet]="pinchAndZoomInstructionTemplate"
    ></ng-container>
    <ng-container *ngIf="hd && !allowReal" [ngTemplateOutlet]="fullSizeLimitationTemplate"></ng-container>
  </div>

  <ng-container [ngTemplateOutlet]="zoomModesTemplate" [ngTemplateOutletContext]="{ real }"></ng-container>
</ng-template>

<ng-template #nonTouchDeviceTemplate let-hd="hd" let-real="real">
  <div class="position-relative w-100 h-100 d-flex justify-content-center align-items-center">
    <ng-container *ngIf="!hd && !real" [ngTemplateOutlet]="hdImageLoadingTemplate"></ng-container>
    <ng-container *ngIf="hd && !real && allowReal" [ngTemplateOutlet]="realThumbnailLoadingTemplate"></ng-container>

    <ng-container *ngIf="!isVeryLargeImage">
      <img
        *ngIf="hd && (!real || !allowReal)"
        [src]="hd"
        [style.max-width.px]="maxDisplayWidth"
        class="hd-image"
        alt=""
      />

      <!-- For GIFs on non-touch devices, we can use ngx-image-zoom as it works well there -->
      <lib-ngx-image-zoom
        #ngxImageZoom
        *ngIf="real && allowReal"
        [circularLens]="true"
        [class.lens-enabled]="enableLens"
        [class.zoom-frozen]="zoomFrozen"
        [class.zooming-enabled]="zoomingEnabled"
        [enableLens]="enableLens"
        [enableScrollZoom]="true"
        [fullImage]="real"
        [lensHeight]="zoomLensSize"
        [lensWidth]="zoomLensSize"
        [maxZoomRatio]="maxZoom"
        [scrollStepSize]="0.05"
        [thumbImage]="hd"
        [zoomMode]="enableLens ? 'toggle-click' : 'click'"
        (imagesLoaded)="onImagesLoaded($event)"
        (zoomPosition)="setZoomPosition($event)"
        (zoomScroll)="setZoomScroll($event)"
        class="image-zoom"
      >
      </lib-ngx-image-zoom>

      <div
        *ngIf="!isMeasuringMode && !isAnnotationMode"
        [class.beyond-one]="Math.round(zoomScroll * 100) >= 101"
        [class.frozen]="zoomFrozen"
        (mouseenter)="setMouseOverUIElement(true)"
        (mouseleave)="setMouseOverUIElement(false)"
        class="image-zoom-indicator"
      >
        <a (click)="snapTo1x()" astrobinEventPreventDefault href="#">
          {{ zoomScroll | number: "1.2-2" }}x
          <fa-icon
            *ngIf="zoomFrozen"
            class="ms-1"
            icon="lock"
            ngbTooltip="{{ 'Zoom is frozen (F)' | translate }}"
          ></fa-icon>
        </a>
      </div>
    </ng-container>

    <ng-container *ngIf="isVeryLargeImage">
      <img *ngIf="!real" [src]="hd" [style.max-width.px]="maxDisplayWidth" class="hd-image" alt="" />

      <a
        *ngIf="hd && real && realThumbnailUnsafeUrl"
        [href]="realThumbnailUnsafeUrl"
        class="no-external-link-icon d-flex align-items-center"
        target="_blank"
      >
        <img [src]="hd" [style.max-width.px]="maxDisplayWidth" class="hd-image" alt="" />
      </a>
      <ng-container
        *ngIf="hd && real && allowReal && realThumbnailUnsafeUrl"
        [ngTemplateOutlet]="imageTooLargeToZoomTemplate"
      ></ng-container>
    </ng-container>

    <ng-container *ngIf="hd && !allowReal" [ngTemplateOutlet]="fullSizeLimitationTemplate"></ng-container>

    <ng-container *ngIf="!isVeryLargeImage && real && allowReal">
      <!-- Zoom-related instructions - only show when not in measuring or annotation mode -->
      <ng-container *ngIf="!isMeasuringMode && !isAnnotationMode">
        <ng-container *ngIf="zoomFrozen" [ngTemplateOutlet]="zoomFrozenInstructionTemplate"></ng-container>

        <ng-container
          *ngIf="!zoomFrozen && !zoomingEnabled && !showZoomIndicator"
          [ngTemplateOutlet]="clickAndScrollInstructionTemplate"
        ></ng-container>

        <ng-container
          *ngIf="!zoomFrozen && zoomingEnabled && !showZoomIndicator && enableLens"
          [ngTemplateOutlet]="clickAgainToCloseLensInstructionTemplate"
        ></ng-container>
      </ng-container>

      <!-- Measurement instructions - only use the first point instruction here,
           the measuring tool component will handle the second point instruction -->
      <ng-container *ngIf="isMeasuringMode">
        <ng-container
          *ngIf="!measurementInProgress"
          [ngTemplateOutlet]="measureFirstPointInstructionTemplate"
        ></ng-container>

        <ng-container
          *ngIf="measurementInProgress"
          [ngTemplateOutlet]="measureSecondPointInstructionTemplate"
        ></ng-container>
      </ng-container>
    </ng-container>
  </div>

  <ng-container [ngTemplateOutlet]="zoomModesTemplate" [ngTemplateOutletContext]="{ real }"></ng-container>
</ng-template>

<ng-template #zoomModesTemplate let-real="real">
  <div *ngIf="!isVeryLargeImage && allowReal" class="fullscreen-view-menu">
    <!-- Kebab menu button - hidden during measuring or annotation mode -->
    <div
      *ngIf="!isMeasuringMode && !isAnnotationMode"
      (mouseenter)="setMouseOverUIElement(true)"
      (mouseleave)="setMouseOverUIElement(false)"
      class="kebab-menu-container"
    >
      <button [attr.aria-expanded]="showKebabMenu" (click)="toggleKebabMenu($event)" class="kebab-button">
        <fa-icon icon="ellipsis-vertical"></fa-icon>
      </button>

      <!-- Dropdown menu -->
      <div [class.show]="showKebabMenu" class="kebab-dropdown">
        <!-- Keyboard shortcuts button - hide on touch devices -->
        <div *ngIf="!isTouchDevice" (click)="showShortcutsDialog($event)" class="kebab-menu-item">
          <fa-icon icon="keyboard"></fa-icon>
          <span>{{ "Keyboard shortcuts" | translate }}</span>
        </div>

        <div *ngIf="!isTouchDevice" class="dropdown-divider"></div>

        <!-- Touch/Mouse mode toggle -->
        <div
          *ngIf="isHybridPC && !isGif && allowZoom"
          (click)="setTouchMouseMode(!touchMode); toggleKebabMenu($event)"
          class="kebab-menu-item"
        >
          <ng-container *ngIf="touchMode">
            <fa-icon icon="hand-pointer"></fa-icon>
            <span>{{ "Touch mode" | translate }}</span>
          </ng-container>
          <ng-container *ngIf="!touchMode">
            <fa-icon icon="mouse-pointer"></fa-icon>
            <span>{{ "Mouse mode" | translate }}</span>
          </ng-container>
        </div>

        <!-- Lens/Zoom mode toggle -->
        <div
          *ngIf="!touchMode && allowZoom"
          (click)="toggleEnableLens($event); toggleKebabMenu($event)"
          class="kebab-menu-item"
        >
          <ng-container *ngIf="enableLens">
            <fa-icon icon="expand"></fa-icon>
            <span>{{ "Zoom mode" | translate }}</span>
          </ng-container>
          <ng-container *ngIf="!enableLens">
            <fa-icon icon="magnifying-glass"></fa-icon>
            <span>{{ "Lens mode" | translate }}</span>
          </ng-container>
        </div>

        <!-- Coordinates toggle - only visible for images with advanced solution -->
        <div
          *ngIf="!isGif && hasAdvancedSolution && !isTouchDevice"
          [class.disabled]="enableLens && zoomingEnabled"
          (click)="toggleCoordinates(); toggleKebabMenu($event)"
          class="kebab-menu-item"
        >
          <fa-icon icon="crosshairs"></fa-icon>
          <span *ngIf="showCoordinates">{{ "Hide coordinates" | translate }}</span>
          <span *ngIf="!showCoordinates">{{ "Show coordinates" | translate }}</span>
        </div>

        <!-- Measuring tool toggle - only available for images with advanced solution -->
        <div
          *ngIf="!isGif"
          [class.active]="isMeasuringMode"
          [class.disabled]="!hasAdvancedSolutionMatrix"
          [ngbTooltip]="
            !hasAdvancedSolutionMatrix
              ? ('The measuring tool is only available on images solved with PixInsight.' | translate)
              : null
          "
          (click)="toggleMeasuringMode($event); toggleKebabMenu($event)"
          class="kebab-menu-item"
        >
          <fa-icon icon="ruler"></fa-icon>
          <span>{{ "Measure distances" | translate }}</span>
        </div>

        <!-- Annotation tool toggle - available for images that are not videos or GIFs -->
        <div
          *ngIf="!isGif && !revision?.videoFile"
          [class.active]="isAnnotationMode"
          (click)="toggleAnnotationMode($event); toggleKebabMenu($event)"
          class="kebab-menu-item"
        >
          <fa-icon icon="file-text"></fa-icon>
          <span>{{ "Annotate image" | translate }}</span>
        </div>
      </div>
    </div>
  </div>
</ng-template>

<div
  *ngIf="show"
  @fadeInOut
  [class.annotation-mode]="isAnnotationMode"
  [class.measuring-mode]="isMeasuringMode"
  [class.touch]="touchMode"
  (mouseleave)="clearCoordinates()"
  (mousemove)="onGlobalMouseMove($event)"
  (touchend)="onTouchEnd($event)"
  (touchmove)="onTouchMove($event)"
  (touchstart)="onTouchStart($event)"
  (wheel)="onGlobalWheel($event)"
  class="fullscreen-image-viewer h-100"
>
  <!-- Measuring tool loading indicator -->
  <div
    *ngIf="pendingMeasurementToolActivation && (!isStaticImageLoaded || !advancedSolutionMatrix)"
    class="measuring-tool-loading-overlay"
  >
    <div class="measuring-tool-loading-indicator">
      <astrobin-loading-indicator></astrobin-loading-indicator>
      <div class="measuring-tool-loading-text">
        {{ "Loading measuring tool..." | translate }}
      </div>
    </div>
  </div>

  <astrobin-measuring-tool
    *ngIf="isMeasuringMode && isStaticImageLoaded && advancedSolutionMatrix"
    [active]="isMeasuringMode"
    [advancedSolutionMatrix]="advancedSolutionMatrix"
    [imageElement]="staticImageEl?.nativeElement"
    [naturalHeight]="revision?.h"
    [naturalWidth]="revision?.w"
    [setMouseOverUIElement]="setMouseOverUIElement.bind(this)"
    [solution]="revision?.solution"
    [windowHeight]="isBrowser ? windowRef.nativeWindow.innerHeight : 0"
    [windowWidth]="isBrowser ? windowRef.nativeWindow.innerWidth : 0"
    (exitMeasuringMode)="toggleMeasuringMode()"
    (measurementComplete)="measurementInProgress = false"
    (measurementStarted)="measurementInProgress = true"
    class="measure-annotation-overlay"
  ></astrobin-measuring-tool>

  <!-- Annotation tool component -->
  <astrobin-annotation-tool
    *ngIf="isAnnotationMode && isStaticImageLoaded"
    [active]="isAnnotationMode"
    [imageElement]="staticImageEl?.nativeElement"
    [imageId]="id"
    [isImageOwner]="isImageOwner"
    [isRegularView]="false"
    [naturalHeight]="revision?.h"
    [naturalWidth]="revision?.w"
    [readOnly]="annotationReadOnlyMode"
    [revision]="revision"
    [setMouseOverUIElement]="setMouseOverUIElement.bind(this)"
    [windowHeight]="isBrowser ? windowRef.nativeWindow.innerHeight : 0"
    [windowWidth]="isBrowser ? windowRef.nativeWindow.innerWidth : 0"
    (annotationsSaved)="onAnnotationsSaved($event)"
    (exitAnnotationMode)="onExitAnnotationMode()"
    class="measure-annotation-overlay"
  ></astrobin-annotation-tool>

  <!-- Coordinates tracker directive -->
  <div
    *ngIf="showCoordinates && !isGif && hasAdvancedSolutionMatrix"
    [eventElement]="imageElementForCoordinateTracker"
    [showGalactic]="true"
    [solutionMatrix]="advancedSolutionMatrix"
    (coords)="onCoordsChange($event, $event?.event)"
    astrobinCoordinatesTracker
  ></div>

  <!-- Coordinate display -->
  <div
    *ngIf="!isTouchDevice && showCoordinates && mouseCoords && !isGif && hasAdvancedSolutionMatrix"
    (mouseenter)="setMouseOverUIElement(true)"
    (mouseleave)="setMouseOverUIElement(false)"
    class="coordinates-wrapper"
  >
    <astrobin-coordinates-display
      [decHtml]="mouseCoords?.decHtml"
      [galacticDecHtml]="mouseCoords?.galacticDecHtml"
      [galacticRaHtml]="mouseCoords?.galacticRaHtml"
      [raHtml]="mouseCoords?.raHtml"
      [showAttribution]="true"
      [showGalactic]="true"
    ></astrobin-coordinates-display>
  </div>

  <!-- Crosshair rulers -->
  <div *ngIf="showCoordinates && mouseCoords && !isGif && hasAdvancedSolutionMatrix" class="crosshair-container">
    <div [style.top.px]="mouseY" class="crosshair-h"></div>
    <div [style.left.px]="mouseX" class="crosshair-v"></div>
  </div>

  <ng-container
    *ngIf="{
      hd: hdThumbnail,
      real: realThumbnail
    } as thumbnails"
  >
    <!-- Always render the touch or non-touch canvas-based viewer in the background -->
    <ng-container
      *ngIf="touchMode && !isGif"
      [ngTemplateOutlet]="touchDeviceTemplate"
      [ngTemplateOutletContext]="{ hd: thumbnails.hd, real: thumbnails.real }"
    >
    </ng-container>

    <ng-container *ngIf="!touchMode || isGif">
      <ng-container
        [ngTemplateOutlet]="nonTouchDeviceTemplate"
        [ngTemplateOutletContext]="{ hd: thumbnails.hd, real: thumbnails.real }"
      >
      </ng-container>
    </ng-container>

    <!-- Show the static template on top when in measuring or annotation mode, when zoom is disabled, or when measuring tool activation is pending -->
    <ng-container
      *ngIf="isMeasuringMode || isAnnotationMode || !allowZoom || pendingMeasurementToolActivation"
      class="static-overlay"
    >
      <ng-container
        [ngTemplateOutlet]="staticTemplate"
        [ngTemplateOutletContext]="{ hd: thumbnails.hd, real: thumbnails.real }"
      >
      </ng-container>
    </ng-container>
  </ng-container>

  <div
    *ngIf="deviceService.mdMin() || (!isMeasuringMode && !isAnnotationMode)"
    (click)="hide($event, { forceExitFullscreen: true })"
    (mouseenter)="setMouseOverUIElement(true)"
    (mouseleave)="setMouseOverUIElement(false)"
    class="close"
  >
    <fa-icon icon="down-left-and-up-right-to-center"></fa-icon>
  </div>

  <div
    *ngIf="!isMeasuringMode && !isAnnotationMode"
    (mouseenter)="setMouseOverUIElement(true)"
    (mouseleave)="setMouseOverUIElement(false)"
    class="astrobin-logo"
  >
    <a routerLink="/">
      <img alt="AstroBin" src="/assets/images/nav-logo.png?v=3" />
    </a>
  </div>

  <!-- Custom Keyboard Shortcuts Overlay -->
  <div
    *ngIf="showKeyboardShortcutsOverlay"
    (mouseenter)="setMouseOverUIElement(true)"
    (mouseleave)="setMouseOverUIElement(false)"
    class="keyboard-shortcuts-overlay"
  >
    <div class="shortcuts-header">
      <strong>{{ "Keyboard Shortcuts" | translate }}</strong>
      <button (click)="hideShortcutsOverlay()" class="btn-close-shortcuts">
        <fa-icon icon="times"></fa-icon>
      </button>
    </div>
    <ul class="shortcuts-list">
      <li>
        <kbd>Z</kbd> <span>{{ "Toggle Lens/Zoom mode" | translate }}</span>
      </li>
      <li *ngIf="hasAdvancedSolution">
        <kbd>C</kbd> <span>{{ "Toggle coordinates display" | translate }}</span>
      </li>
      <li>
        <kbd>D</kbd> <span>{{ "Toggle distance measuring tool" | translate }}</span>
      </li>
      <li>
        <kbd>N</kbd> <span>{{ "Toggle annotation tool" | translate }}</span>
      </li>
      <li>
        <kbd>F</kbd> <span>{{ "Freeze/unfreeze zoom position" | translate }}</span>
      </li>
      <li>
        <kbd>ESC</kbd> <span>{{ "Exit current mode or close image" | translate }}</span>
      </li>
    </ul>
  </div>
</div>
